import type { QueryObserverModel } from '.';
import { effect, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { QueryObserver } from '@tanstack/query-core';
import type { QueryObserverOptions, QueryObserverResult } from '@tanstack/query-core';
import { inject, injectable } from 'src/features/ioc';
import type { ReactBindings } from 'src/types/bindings';

type OptionsInput<TQueryFnData, TError, TQueryData> =
    | QueryObserverOptions<TQueryFnData, TError, TQueryData>
    | ReadonlySignal<QueryObserverOptions<TQueryFnData, TError, TQueryData>>;

@injectable()
export class QueryObserverModelImpl<TQueryFnData, TError, TQueryData> implements QueryObserverModel<
    TQueryFnData,
    TError,
    TQueryData
> {
    readonly #log: ReactBindings['Logger'];
    constructor(
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('Logger') log: ReactBindings['Logger'],
    ) {
        this.#log = log.getSubLogger({ name: 'QueryObserverModelImpl' });
    }

    /**
     * Resources associated with this instance
     */
    #disposableStack = new DisposableStack();

    /**
     * @deprecated Read from queryModel.signal instead
     */
    getCurrentResult() {
        return this.#query.getCurrentResult();
    }

    init(options: OptionsInput<TQueryFnData, TError, TQueryData>) {
        const isSignal = 'value' in options && 'subscribe' in options;
        const initialOptions = isSignal ? options.value : options;

        this.#query = new QueryObserver(this.queryClient, initialOptions);
        this.#disposableStack.defer(() => this.#query.destroy());

        this.#signal = signal(this.#query.getCurrentResult());

        if (TRACE) {
            effect(() => {
                const result = this.#signal.value;
                if (result.error) {
                    this.#log.error('[QueryObserverModel] Query result updated with error:', {
                        error: result.error,
                        data: result.data,
                        isLoading: result.isLoading,
                        isFetching: result.isFetching,
                    });
                }
            });
        }

        this.#disposableStack.defer(this.#query.subscribe((result) => (this.#signal.value = result)));

        // React to options changes if a signal was provided
        if (isSignal) {
            this.#disposableStack.defer(
                effect(() => {
                    this.#query.setOptions(options.value);
                }),
            );
        }
    }

    #query!: QueryObserver<TQueryFnData, TError, TQueryData>;

    get queryObserver() {
        return this.#query;
    }

    setQueryOptions(options: QueryObserverOptions<TQueryFnData, TError, TQueryData>) {
        this.#query.setOptions(options);
    }

    #signal!: Signal<QueryObserverResult<TQueryData, TError>>;

    get signal(): ReadonlySignal<QueryObserverResult<TQueryData, TError>> {
        return this.#signal;
    }

    /**
     * @deprecated Read from queryModel.signal instead
     */
    subscribe(callback: (result: QueryObserverResult<TQueryData, TError>) => void) {
        const unsubscribe = this.#query.subscribe(callback);

        // This is slightly wonky, but we ensure the subscriber is called once initially with the current value
        callback(this.#query.getCurrentResult());

        const disposable = {
            [Symbol.dispose]: () => unsubscribe(),
        };

        this.#disposableStack.use(disposable);

        return disposable;
    }

    [Symbol.dispose]() {
        this.#disposableStack?.dispose();
    }
}
